import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
  CreditCard,
  Banknote,
  X,
  CheckCircle2,
  Search,
  ArrowLeft,
  QrCode,
  Package,
  Shirt,
  Tag
} from "lucide-react";
import api from "../api/api";
import { formatDateTime } from "../utils/datetime";

// Helper for haptic feedback
const triggerHaptic = (style = "light") => {
  if (typeof navigator !== "undefined" && navigator.vibrate) {
    if (style === "light") navigator.vibrate(30);
    else if (style === "medium") navigator.vibrate(50);
    else if (style === "heavy") navigator.vibrate([50, 50, 50]);
    else if (style === "success") navigator.vibrate([30, 50, 30]);
    else if (style === "error") navigator.vibrate([50, 30, 50, 30, 50]);
  }
};

function Kasir() {
  const parseTiers = (tiersData) => {
    try {
      if (typeof tiersData === 'string') return JSON.parse(tiersData) || [];
      if (Array.isArray(tiersData)) return tiersData;
    } catch(e) {}
    return [];
  };

  const calcSubtotal = (item) => {
    const tiers = parseTiers(item.promo_tiers).sort((a, b) => b.min_qty - a.min_qty);
    let remainingQty = item.cartQty;
    let subtotal = 0;
    for (const tier of tiers) {
      if (remainingQty >= tier.min_qty) {
        const promoGroups = Math.floor(remainingQty / tier.min_qty);
        subtotal += (promoGroups * tier.price);
        remainingQty = remainingQty % tier.min_qty;
      }
    }
    subtotal += (remainingQty * item.price);
    return subtotal;
  };
  
  const getActivePromoTier = (item) => {
    const tiers = parseTiers(item.promo_tiers).sort((a, b) => b.min_qty - a.min_qty);
    return tiers.find(t => item.cartQty >= t.min_qty);
  };

  const getDirectPromo = (item) => {
    const tiers = parseTiers(item.promo_tiers);
    return tiers.find(t => t.min_qty === 1);
  };

  const { sessionId } = useParams();
  const navigate      = useNavigate();

  const [session, setSession]       = useState(null);
  const [products, setProducts]     = useState([]);
  const [cart, setCart]             = useState([]);
  const [search, setSearch]         = useState("");
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState("");
  const [payModal, setPayModal]     = useState(false);
  const [closeModal, setCloseModal] = useState(false);
  const [lastTx, setLastTx]         = useState(null);

  const [activeCategory, setActiveCategory] = useState("All");

  // History Tab State
  const [activeTab, setActiveTab]   = useState("cart"); // "cart" | "history"
  const [transactions, setTransactions] = useState([]);
  const [loadingTx, setLoadingTx]   = useState(false);

  const fetchSession = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/kasir/sessions/${sessionId}`);
      setSession(res.data);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.message || "Gagal mengambil data sesi");
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const fetchProducts = useCallback(async (eventId) => {
    try {
      const res = await api.get(`/kasir/events/${eventId}/products`);
      setProducts(res.data || []);
    } catch (e) {
      setError("Gagal mengambil produk");
    }
  }, []);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  const fetchTransactions = useCallback(async () => {
    if (!sessionId) return;
    try {
      setLoadingTx(true);
      const res = await api.get(`/kasir/sessions/${sessionId}/transactions`);
      setTransactions(res.data?.data || []);
    } catch (e) {
      console.error("Gagal load history:", e);
    } finally {
      setLoadingTx(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (activeTab === "history") {
      fetchTransactions();
    }
  }, [activeTab, fetchTransactions]);

  const handleVoidTransaction = async (txId) => {
    triggerHaptic("heavy");
    if (!window.confirm("Yakin ingin membatalkan (void) transaksi ini? Stok akan dikembalikan.")) return;
    try {
      setLoadingTx(true);
      await api.delete(`/kasir/transactions/${txId}`);
      await fetchTransactions();
      if (session?.event_id) {
        await fetchProducts(session.event_id);
      }
      triggerHaptic("success");
    } catch (e) {
      triggerHaptic("error");
      alert(e?.response?.data?.message || "Gagal void transaksi");
    } finally {
      setLoadingTx(false);
    }
  };

  useEffect(() => {
    if (session?.event_id) {
      fetchProducts(session.event_id);
    }
  }, [session?.event_id, fetchProducts]);

  // Derived Categories
  const categories = useMemo(() => {
    const cats = new Set();
    products.forEach(p => {
      if (p.product_name) {
        const firstWord = p.product_name.split(' ')[0].toUpperCase();
        cats.add(firstWord);
      }
    });
    return ["All", ...Array.from(cats)].slice(0, 7); // Limit to top categories
  }, [products]);

  // ── Cart helpers ──────────────────────────────────────────
  const addToCart = (product) => {
    triggerHaptic("light");
    setCart((prev) => {
      const existing = prev.find((c) => c.id === product.id);
      if (existing) {
        if (existing.cartQty >= product.qty_remaining) {
          triggerHaptic("error");
          return prev;
        }
        return prev.map((c) =>
          c.id === product.id ? { ...c, cartQty: c.cartQty + 1 } : c
        );
      }
      return [...prev, { ...product, cartQty: 1 }];
    });
  };

  const changeQty = (id, delta) => {
    triggerHaptic("light");
    setCart((prev) =>
      prev
        .map((c) => (c.id === id ? { ...c, cartQty: Math.max(0, c.cartQty + delta) } : c))
        .filter((c) => c.cartQty > 0)
    );
  };

  const removeFromCart = (id) => {
    triggerHaptic("medium");
    setCart((prev) => prev.filter((c) => c.id !== id));
  };
  
  const clearCart = () => { 
    setCart([]); 
    setManualDiscount({ type: "none", value: "" }); 
    setOrderNotes(""); 
  };

  // ── Manual Discount & Notes ──────────────────────────────────
  const [manualDiscount, setManualDiscount] = useState({ type: "none", value: "" }); // type: 'none' | 'percent' | 'fixed' | 'free'
  const [orderNotes, setOrderNotes] = useState("");

  const cartBaseTotal = cart.reduce((s, c) => s + (Number(c.price) * Number(c.cartQty)), 0);
  const cartPromoTotal = cart.reduce((s, c) => s + calcSubtotal(c), 0);
  const promoDiscount = cartBaseTotal - cartPromoTotal;

  // Calculate manual discount amount
  const manualDiscountAmount = (() => {
    if (manualDiscount.type === "free") return cartPromoTotal;
    if (manualDiscount.type === "percent") {
      const pct = Math.min(100, Math.max(0, Number(manualDiscount.value) || 0));
      return Math.round(cartPromoTotal * pct / 100);
    }
    if (manualDiscount.type === "fixed") {
      return Math.min(cartPromoTotal, Math.max(0, Number(manualDiscount.value) || 0));
    }
    return 0;
  })();

  const cartTotal = cartPromoTotal - manualDiscountAmount;
  const cartCount = cart.reduce((s, c) => s + c.cartQty, 0);

  const filtered = products.filter((p) => {
    const q = search.toLowerCase();
    const matchSearch = (p.product_name || "").toLowerCase().includes(q) || (p.product_code || "").toLowerCase().includes(q);
    const matchCat = activeCategory === "All" || (p.product_name && p.product_name.toUpperCase().startsWith(activeCategory));
    return matchSearch && matchCat;
  });

  const outOfStockCount = products.filter(p => p.qty_remaining <= 0).length;

  const handlePay = async ({ paymentMethod, amountPaid }) => {
    try {
      const items = cart.map((c) => ({ event_stock_id: c.id, qty: c.cartQty }));
      const res = await api.post(`/kasir/sessions/${sessionId}/transactions`, {
        items,
        payment_method: paymentMethod,
        amount_paid: amountPaid,
        discount_amount: manualDiscountAmount,
        notes: orderNotes || undefined,
      });
      triggerHaptic("success");
      setLastTx({ ...res.data?.result, paymentMethod, amountPaid, manualDiscountAmount, orderNotes });
      clearCart();
      setPayModal(false);
      if (session?.event_id) fetchProducts(session.event_id);
    } catch (e) {
      triggerHaptic("error");
      alert(e?.response?.data?.message || "Transaksi gagal");
    }
  };

  const handleCloseSession = async () => {
    triggerHaptic("medium");
    try {
      await api.post(`/kasir/sessions/${sessionId}/close`);
      navigate(`/rekap/${sessionId}`);
    } catch (e) {
      triggerHaptic("error");
      alert(e?.response?.data?.message || "Gagal menutup kasir");
    }
  };

  if (loading && !session) {
    return (
      <div className="flex h-screen items-center justify-center bg-zinc-50">
        <div className="flex flex-col items-center gap-3">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-zinc-200 border-t-zinc-900" />
          <span className="text-sm font-medium text-zinc-500">Memuat Sistem Kasir...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-zinc-50 font-sans selection:bg-zinc-200">
      {/* Top Header */}
      <div className="flex items-center justify-between border-b border-zinc-200 bg-white px-6 py-4 z-10">
        <div className="flex items-center gap-4">
          <button
            onClick={() => { triggerHaptic("light"); navigate(`/events/${session?.event_id || ""}`); }}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-zinc-200 bg-white hover:bg-zinc-50 transition-colors focus:outline-none focus:ring-1 focus:ring-zinc-900"
          >
            <ArrowLeft className="h-4 w-4 text-zinc-600" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-zinc-900 tracking-tight">
              {session?.event_name || "Point of Sale"}
            </h1>
            <div className="font-mono text-[11px] font-medium text-zinc-500">Sesi #{sessionId}</div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            <input
              className="h-9 w-full rounded-md border border-zinc-300 bg-white pl-9 pr-4 text-[13px] font-medium text-zinc-900 placeholder:text-zinc-400 outline-none transition-all focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 shadow-sm"
              placeholder="Cari menu atau kategori..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {outOfStockCount > 0 && (
            <div className="hidden sm:block text-[11px] font-bold text-rose-600 bg-rose-50 px-2 py-1 rounded-md ring-1 ring-inset ring-rose-600/20">
              {outOfStockCount} HABIS
            </div>
          )}
          <button
            onClick={() => { triggerHaptic("light"); setCloseModal(true); }}
            className="rounded-md bg-zinc-900 px-4 py-2 text-[13px] font-medium text-white shadow-sm hover:bg-zinc-800 transition-colors focus:outline-none focus:ring-1 focus:ring-zinc-900"
          >
            Tutup Kasir
          </button>
        </div>
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* ── Left Area: Product Grid ───────────────────────────── */}
        <div className="flex flex-1 flex-col overflow-hidden bg-zinc-50 px-6">
          {/* Categories */}
          <div className="flex items-center gap-2 py-5 overflow-x-auto hide-scrollbar shrink-0">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => { triggerHaptic("light"); setActiveCategory(cat); }}
                className={`flex items-center gap-2 shrink-0 rounded-md px-4 py-2 text-[13px] font-semibold transition-all focus:outline-none focus:ring-1 focus:ring-zinc-900 ${
                  activeCategory === cat
                    ? "bg-zinc-900 text-white shadow-sm"
                    : "bg-white text-zinc-600 border border-zinc-200 hover:bg-zinc-50 shadow-sm"
                }`}
              >
                {cat === 'All' ? <Package className="h-4 w-4" /> : <Shirt className="h-4 w-4" />}
                {cat}
              </button>
            ))}
          </div>

          <div className="mb-4 flex items-center justify-between shrink-0">
            <h2 className="text-lg font-bold text-zinc-900">Pilih Produk</h2>
            <span className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">{filtered.length} Tersedia</span>
          </div>

          {error && (
            <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] font-medium text-rose-700 shrink-0">
              {error}
            </div>
          )}

          <div className="flex-1 overflow-y-auto pb-8 hide-scrollbar">
            {!session ? (
              <div className="py-12 text-center text-sm text-zinc-400">Memuat sesi...</div>
            ) : filtered.length === 0 ? (
              <div className="py-12 flex flex-col items-center gap-3 text-center text-zinc-400">
                <Search className="h-8 w-8 text-zinc-300" />
                <div className="text-[13px] font-medium">
                  {search ? "Produk tidak ditemukan." : "Tidak ada produk tersedia."}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                {filtered.map((p) => {
                  const inCart    = cart.find((c) => c.id === p.id);
                  const remaining = p.qty_remaining - (inCart?.cartQty || 0);
                  const soldOut   = remaining <= 0;

                  return (
                    <button
                      key={p.id}
                      onClick={() => !soldOut && addToCart(p)}
                      disabled={soldOut}
                      className={`group relative flex flex-col items-center rounded-lg border bg-white p-4 text-center transition-all duration-200 ${
                        soldOut
                          ? "border-zinc-200 opacity-50 grayscale cursor-not-allowed"
                          : inCart
                          ? "border-zinc-900 ring-1 ring-zinc-900 shadow-md scale-[0.98]"
                          : "border-zinc-200 hover:border-zinc-400 shadow-sm hover:shadow active:scale-[0.98]"
                      }`}
                    >
                      {(() => {
                        const directPromo = getDirectPromo(p);
                        if (directPromo) {
                          const pct = p.price > 0 ? Math.round(((p.price - directPromo.price) / p.price) * 100) : 0;
                          return (
                            <span className="absolute -left-2 -top-2 z-10 rotate-[-4deg] inline-flex items-center rounded bg-rose-600 px-2 py-0.5 text-[10px] font-bold tracking-widest text-white shadow-sm ring-2 ring-white">
                              {pct}% OFF
                            </span>
                          );
                        }
                        return null;
                      })()}
                      {inCart && (
                        <span className="absolute -right-2 -top-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-zinc-900 text-[11px] font-bold text-white shadow-sm ring-2 ring-white">
                          {inCart.cartQty}
                        </span>
                      )}
                      
                      {/* Product Image */}
                      <div className="mb-3 flex h-32 w-full items-center justify-center overflow-hidden rounded-md bg-zinc-50 border border-zinc-100 transition-opacity">
                        {p.image_url ? (
                          <img src={p.image_url} alt={p.product_name} className="h-full w-full object-cover" />
                        ) : (
                          <Shirt className="h-10 w-10 text-zinc-300" />
                        )}
                      </div>
                      
                      <div className="w-full">
                        <div 
                          className="line-clamp-2 text-[13px] font-bold text-zinc-900 mb-1.5 leading-tight min-h-[36px]"
                          title={p.product_name}
                        >
                          {p.product_name}
                        </div>
                        {(() => {
                          const directPromo = getDirectPromo(p);
                          if (directPromo) {
                            return (
                              <div className="flex flex-col items-center">
                                <div className="text-[10px] font-medium text-zinc-400 line-through decoration-zinc-300">
                                  {fmtRp(p.price)}
                                </div>
                                <div className="text-sm font-bold text-emerald-600 tabular-nums">
                                  {fmtRp(directPromo.price)}
                                </div>
                              </div>
                            );
                          }
                          return (
                            <div className="text-sm font-bold text-zinc-900 tabular-nums">
                              {fmtRp(p.price)}
                            </div>
                          );
                        })()}
                        <div className={`mt-2.5 text-[10px] font-bold uppercase tracking-wider rounded-md py-1 px-2 w-fit mx-auto ${
                            soldOut ? "text-zinc-500 bg-zinc-100" : remaining <= 3 ? "text-rose-700 bg-rose-50 ring-1 ring-inset ring-rose-600/20" : "text-zinc-600 bg-zinc-50 ring-1 ring-inset ring-zinc-500/20"
                          }`}>
                          {soldOut ? "Stok Habis" : `Sisa ${remaining}`}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── Right Area: Cart Panel ──────────────────────────────── */}
        <div className="flex w-[400px] shrink-0 flex-col bg-white shadow-[-4px_0_24px_rgba(0,0,0,0.02)] min-h-0 relative z-20 border-l border-zinc-200">
          
          <div className="flex items-center justify-between px-6 pt-6 pb-4">
            <div>
              <h2 className="text-base font-bold text-zinc-900">Pesanan Aktif</h2>
              <div className="font-mono text-[11px] font-medium text-zinc-500 mt-0.5">#{sessionId}{Date.now().toString().slice(-4)}</div>
            </div>
            
            {/* Tabs for Cart vs History */}
            <div className="flex rounded-md bg-zinc-100 p-0.5">
              <button
                onClick={() => { triggerHaptic("light"); setActiveTab("cart"); }}
                className={`rounded px-3 py-1 text-[11px] font-bold transition-all ${
                  activeTab === "cart" ? "bg-white text-zinc-900 shadow-sm ring-1 ring-zinc-200" : "text-zinc-500 hover:text-zinc-900"
                }`}
              >
                Keranjang
              </button>
              <button
                onClick={() => { triggerHaptic("light"); setActiveTab("history"); }}
                className={`rounded px-3 py-1 text-[11px] font-bold transition-all ${
                  activeTab === "history" ? "bg-white text-zinc-900 shadow-sm ring-1 ring-zinc-200" : "text-zinc-500 hover:text-zinc-900"
                }`}
              >
                Riwayat
              </button>
            </div>
          </div>

          {activeTab === "cart" ? (
            <div className="flex flex-col flex-1 min-h-0">
              <div className="flex-1 overflow-y-auto px-6 py-2 hide-scrollbar">
                {cart.length === 0 ? (
                  <div className="flex h-full flex-col items-center justify-center gap-4 text-zinc-400">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-zinc-50 border border-zinc-100">
                      <ShoppingCart className="h-6 w-6 text-zinc-300" />
                    </div>
                    <div className="text-[13px] font-medium">Keranjang masih kosong</div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {cart.map((item) => (
                      <div key={item.id} className="flex gap-3 group">
                        <div className="h-14 w-14 shrink-0 overflow-hidden rounded-md bg-zinc-50 border border-zinc-200 flex items-center justify-center">
                          {item.image_url ? (
                            <img src={item.image_url} alt={item.product_name} className="h-full w-full object-cover" />
                          ) : (
                            <Shirt className="h-5 w-5 text-zinc-300" />
                          )}
                        </div>
                        
                        <div className="flex flex-1 flex-col justify-between py-0.5">
                          <div>
                            <div className="flex justify-between items-start gap-2">
                              <div className="text-[13px] font-bold text-zinc-900 line-clamp-2 leading-tight">
                                {item.product_name}
                              </div>
                              <button onClick={() => removeFromCart(item.id)} className="text-zinc-300 hover:text-rose-500 transition-colors">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                            
                            {(() => {
                              const activeTier = getActivePromoTier(item);
                              return activeTier ? (
                                <div className="text-[10px] font-bold text-emerald-600 mt-0.5 flex items-center gap-1">
                                  <Tag className="h-2.5 w-2.5" /> Promo Aktif
                                </div>
                              ) : (
                                <div className="font-mono text-[10px] font-medium text-zinc-400 mt-0.5">{item.product_code}</div>
                              );
                            })()}
                          </div>
                          
                          <div className="flex items-center justify-between mt-2">
                            <div className="text-[13px] font-bold text-zinc-900 tabular-nums">
                              {fmtRp(calcSubtotal(item))}
                            </div>
                            <div className="flex items-center gap-2 rounded-md border border-zinc-200 bg-white p-0.5 shadow-sm">
                              <button onClick={() => changeQty(item.id, -1)} className="flex h-5 w-5 items-center justify-center rounded bg-zinc-100 hover:bg-zinc-200 text-zinc-600 transition-colors">
                                <Minus className="h-3 w-3" />
                              </button>
                              <span className="w-5 text-center text-[11px] font-bold tabular-nums">
                                {item.cartQty}
                              </span>
                              <button onClick={() => changeQty(item.id, 1)} disabled={item.cartQty >= item.qty_remaining} className="flex h-5 w-5 items-center justify-center rounded bg-zinc-900 hover:bg-zinc-800 text-white disabled:opacity-40 transition-colors">
                                <Plus className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="px-6 pb-6 pt-4 bg-white border-t border-zinc-200 space-y-4">
                {/* ── Manual Discount ─────────────────── */}
                {cart.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Diskon Manual</div>
                    <div className="flex gap-1.5">
                      {[
                        { key: "none", label: "Tak Ada" },
                        { key: "percent", label: "%" },
                        { key: "fixed", label: "Rp" },
                        { key: "free", label: "Gratis" },
                      ].map((opt) => (
                        <button
                          key={opt.key}
                          onClick={() => { triggerHaptic("light"); setManualDiscount({ type: opt.key, value: "" }); }}
                          className={`flex-1 rounded-md px-2 py-1.5 text-[11px] font-bold transition-all ${
                            manualDiscount.type === opt.key
                              ? opt.key === "free" ? "bg-emerald-600 text-white shadow-sm ring-1 ring-emerald-600" : "bg-zinc-900 text-white shadow-sm ring-1 ring-zinc-900"
                              : "bg-white text-zinc-600 border border-zinc-200 hover:bg-zinc-50"
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    {manualDiscount.type === "percent" && (
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          placeholder="0"
                          value={manualDiscount.value}
                          onChange={(e) => setManualDiscount({ ...manualDiscount, value: e.target.value })}
                          className="w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-[13px] font-bold tabular-nums outline-none focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 shadow-sm"
                        />
                        <span className="text-[13px] font-bold text-zinc-500 shrink-0">%</span>
                      </div>
                    )}
                    {manualDiscount.type === "fixed" && (
                      <div className="flex items-center gap-2">
                        <span className="text-[13px] font-bold text-zinc-500 shrink-0">Rp</span>
                        <input
                          type="number"
                          min="0"
                          placeholder="0"
                          value={manualDiscount.value}
                          onChange={(e) => setManualDiscount({ ...manualDiscount, value: e.target.value })}
                          className="w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-[13px] font-bold tabular-nums outline-none focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 shadow-sm"
                        />
                      </div>
                    )}
                    {manualDiscount.type === "free" && (
                      <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-[11px] font-bold text-emerald-700 text-center">
                        🎁 Semua pesanan di-Gratis-kan (Rp 0)
                      </div>
                    )}
                  </div>
                )}

                {/* ── Catatan / Notes ─────────────────── */}
                {cart.length > 0 && (
                  <div className="space-y-1.5">
                    <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Catatan Tambahan</div>
                    <textarea
                      value={orderNotes}
                      onChange={(e) => setOrderNotes(e.target.value)}
                      placeholder="Contoh: Plastik dipisah, defect dsb..."
                      rows={2}
                      className="w-full resize-none rounded-md border border-zinc-300 bg-white px-3 py-2 text-[13px] outline-none focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 shadow-sm placeholder:text-zinc-300"
                    />
                  </div>
                )}

                {/* ── Summary ────────────────────────── */}
                <div className="space-y-2 pt-2">
                  <div className="flex justify-between text-[13px]">
                    <span className="font-medium text-zinc-500">Subtotal ({cartCount} item)</span>
                    <span className="font-semibold text-zinc-700 tabular-nums">{fmtRp(cartBaseTotal)}</span>
                  </div>
                  {promoDiscount > 0 && (
                    <div className="flex justify-between text-[13px]">
                      <span className="font-medium text-zinc-500">Promo Bundling</span>
                      <span className="font-bold text-emerald-600 tabular-nums">- {fmtRp(promoDiscount)}</span>
                    </div>
                  )}
                  {manualDiscountAmount > 0 && (
                    <div className="flex justify-between text-[13px]">
                      <span className="font-medium text-zinc-500">
                        Diskon {manualDiscount.type === "percent" ? `(${manualDiscount.value}%)` : manualDiscount.type === "free" ? "(Gratis)" : "Manual"}
                      </span>
                      <span className="font-bold text-emerald-600 tabular-nums">- {fmtRp(manualDiscountAmount)}</span>
                    </div>
                  )}
                  <div className="border-t border-dashed border-zinc-200 my-1"></div>
                  <div className="flex justify-between items-center py-1">
                    <span className="text-[15px] font-bold text-zinc-900">Total Akhir</span>
                    <span className="text-xl font-bold text-zinc-900 tabular-nums tracking-tight">{fmtRp(cartTotal)}</span>
                  </div>
                </div>
                
                <button 
                  onClick={() => { triggerHaptic("medium"); setPayModal(true); }} 
                  disabled={cart.length === 0} 
                  className="w-full rounded-md bg-zinc-900 py-3 text-[15px] font-bold text-white shadow-sm transition-all hover:bg-zinc-800 active:scale-[0.98] disabled:opacity-40 disabled:scale-100"
                >
                  Proses Pembayaran
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col flex-1 min-h-0 overflow-hidden bg-zinc-50/50">
              <div className="flex-1 overflow-y-auto p-6 space-y-4 hide-scrollbar">
                {loadingTx ? (
                  <div className="py-8 text-center text-[13px] font-medium text-zinc-400">Memuat riwayat...</div>
                ) : transactions.length === 0 ? (
                  <div className="py-8 text-center text-[13px] font-medium text-zinc-400">Belum ada transaksi di sesi ini.</div>
                ) : (
                  transactions.map((tx) => (
                    <div key={tx.id} className="relative overflow-hidden rounded-lg bg-white p-4 shadow-sm border border-zinc-200 group hover:border-zinc-300 transition-colors">
                      <div className="mb-3 flex items-start justify-between border-b border-dashed border-zinc-200 pb-3">
                        <div>
                          <div className="font-bold text-[13px] text-zinc-900">#TX-{tx.id.toString().padStart(4, '0')}</div>
                          <div className="font-mono text-[10px] font-medium text-zinc-400 mt-0.5">{formatDateTime(tx.created_at)}</div>
                        </div>
                        <button onClick={() => handleVoidTransaction(tx.id)} className="text-[11px] font-bold text-rose-600 opacity-0 group-hover:opacity-100 transition-opacity bg-rose-50 hover:bg-rose-100 px-2 py-1 rounded ring-1 ring-inset ring-rose-600/20">
                          Void (Batal)
                        </button>
                      </div>
                      <div className="space-y-2 mb-3 border-b border-dashed border-zinc-200 pb-3">
                        {tx.items?.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-[11px]">
                            <div className="flex-1 font-medium truncate pr-2 text-zinc-700">{item.product_name}</div>
                            <div className="shrink-0 font-bold text-zinc-500 mr-2">{item.qty}x</div>
                            <div className="w-16 shrink-0 text-right font-bold tabular-nums text-zinc-900">{fmtRp(item.subtotal)}</div>
                          </div>
                        ))}
                      </div>
                      <div className="space-y-1.5 text-[11px]">
                        {Number(tx.discount_amount) > 0 && (
                          <div className="flex justify-between text-emerald-600 font-bold">
                            <span>Diskon</span>
                            <span className="tabular-nums">- {fmtRp(tx.discount_amount)}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-zinc-500 font-medium">
                          <span>Total</span>
                          <span className="tabular-nums font-bold text-zinc-900">{fmtRp(tx.total)}</span>
                        </div>
                        <div className="flex justify-between text-zinc-500 font-medium">
                          <span className="uppercase">Paid ({tx.payment_method})</span>
                          <span className="tabular-nums">{fmtRp(tx.amount_paid)}</span>
                        </div>
                        <div className="flex justify-between font-bold text-zinc-900 mt-2 pt-2 border-t border-zinc-100">
                          <span>Kembalian</span>
                          <span className="tabular-nums">{fmtRp(tx.change_given)}</span>
                        </div>
                        {tx.notes && (
                          <div className="mt-2 pt-2 border-t border-zinc-100">
                            <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-0.5">Catatan</div>
                            <div className="text-[11px] text-zinc-600 leading-relaxed italic">{tx.notes}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Modals ─────────────────────────────── */}
      {lastTx && createPortal(
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-zinc-950/60 backdrop-blur-sm p-4 animate-fade-in">
          <div className="w-full max-w-sm rounded-xl bg-white p-8 shadow-2xl animate-fade-in text-center border border-zinc-200">
            <div className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50 ring-4 ring-emerald-50/50">
              <CheckCircle2 className="h-10 w-10 text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-zinc-900 mb-1">Transaksi Berhasil!</h2>
            <p className="text-[13px] font-medium text-zinc-500 mb-6">Pembayaran selesai. Stok otomatis dikurangi.</p>
            
            <div className="rounded-lg bg-zinc-50 border border-zinc-200 p-4 mb-6 space-y-2.5 text-[13px] text-left">
              {lastTx.manualDiscountAmount > 0 && (
                <div className="flex justify-between font-medium">
                  <span className="text-zinc-500">Diskon Total</span>
                  <span className="font-bold tabular-nums text-emerald-600">- {fmtRp(lastTx.manualDiscountAmount)}</span>
                </div>
              )}
              <div className="flex justify-between font-medium">
                <span className="text-zinc-500">Total Dibayar</span>
                <span className="font-bold tabular-nums text-zinc-900">{fmtRp(lastTx.total)}</span>
              </div>
              <div className="flex justify-between font-medium">
                <span className="text-zinc-500">Diterima (Kas)</span>
                <span className="font-bold tabular-nums text-zinc-700">{fmtRp(lastTx.amount_paid)}</span>
              </div>
              <div className="flex justify-between border-t border-dashed border-zinc-300 pt-2.5 mt-2.5">
                <span className="font-bold text-zinc-900">Uang Kembalian</span>
                <span className="font-bold text-emerald-600 tabular-nums text-lg">{fmtRp(lastTx.change)}</span>
              </div>
              {lastTx.orderNotes && (
                <div className="border-t border-dashed border-zinc-300 pt-2.5 mt-1">
                  <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-1">Catatan</div>
                  <div className="text-[11px] text-zinc-600 leading-relaxed italic">{lastTx.orderNotes}</div>
                </div>
              )}
            </div>

            <button 
              onClick={() => { triggerHaptic("light"); setLastTx(null); }} 
              className="w-full rounded-md bg-zinc-900 py-3 font-bold text-white text-[13px] shadow-sm transition-all hover:bg-zinc-800 active:scale-95"
            >
              Selesai & Order Baru
            </button>
          </div>
        </div>,
        document.body
      )}

      {payModal && (
        <PayModal
          total={cartTotal}
          cart={cart}
          onClose={() => { triggerHaptic("light"); setPayModal(false); }}
          onConfirm={handlePay}
        />
      )}

      {closeModal && (
        <ConfirmModal
          title="Tutup Kasir?"
          message="Kasir akan ditutup dan Anda akan diarahkan ke halaman Rekap Harian. Pastikan semua order telah selesai."
          confirmLabel="Tutup Kasir Sekarang"
          confirmTone="rose"
          onClose={() => { triggerHaptic("light"); setCloseModal(false); }}
          onConfirm={handleCloseSession}
        />
      )}
    </div>
  );
}

function PayModal({ total, cart, onClose, onConfirm }) {
  const [method, setMethod]         = useState("cash");
  const [amountPaid, setAmountPaid] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const parsed  = Number(amountPaid) || 0;
  const change  = parsed - total;
  const isValid = total === 0 ? true : method !== "cash" ? true : parsed >= total;

  useEffect(() => {
    // Initial haptic on mount
    triggerHaptic("medium");
  }, []);

  const handleConfirm = async () => {
    triggerHaptic("medium");
    try {
      setSubmitting(true);
      await onConfirm({ paymentMethod: method, amountPaid: method === "cash" ? parsed : total });
    } finally {
      setSubmitting(false);
    }
  };

  const quickAmounts = [
    { label: "Uang Pas", val: total },
    { label: "50rb", val: 50000 },
    { label: "100rb", val: 100000 },
    { label: "250rb", val: 250000 },
  ];

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-end justify-center bg-zinc-950/60 backdrop-blur-sm sm:items-center p-4 transition-all duration-300 animate-fade-in">
      <div className="w-full max-w-sm rounded-xl bg-white shadow-2xl animate-fade-in overflow-hidden border border-zinc-200">
        <div className="flex items-center justify-between border-b border-zinc-100 px-6 py-4">
          <h2 className="text-[15px] font-bold text-zinc-900">Pembayaran</h2>
          <button onClick={onClose} className="rounded-md p-1.5 transition-colors bg-white hover:bg-zinc-100 text-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-900">
            <X className="h-4 w-4" />
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          <div className="text-center">
            <div className="text-[11px] font-bold text-zinc-400 uppercase tracking-widest mb-1">Total Tagihan</div>
            <div className="text-3xl font-bold text-zinc-900 tabular-nums tracking-tight">{fmtRp(total)}</div>
          </div>
          
          <div>
            <label className="mb-2.5 block text-[10px] font-bold uppercase tracking-widest text-zinc-400">
              Metode Pembayaran
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { val: "cash",     label: "Tunai",    icon: Banknote },
                { val: "transfer", label: "Transfer", icon: CreditCard },
                { val: "qris",     label: "QRIS",     icon: QrCode },
              ].map(({ val, label, icon: Icon }) => (
                <button
                  key={val}
                  onClick={() => { triggerHaptic("light"); setMethod(val); }}
                  className={`flex flex-col items-center justify-center gap-2 rounded-md border p-3 text-[11px] font-bold transition-all focus:outline-none focus:ring-1 focus:ring-zinc-900 ${
                    method === val
                      ? "border-zinc-900 bg-zinc-900 text-white shadow-sm"
                      : "border-zinc-200 bg-white text-zinc-500 hover:border-zinc-300 hover:bg-zinc-50"
                  }`}
                >
                  <Icon className={`h-5 w-5 ${method === val ? "text-white" : "text-zinc-400"}`} />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {method === "cash" && (
            <div className="animate-fade-in space-y-4">
              <div>
                <label className="mb-2.5 block text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                  Uang Diterima
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 font-bold text-sm">Rp</div>
                  <input
                    className="w-full rounded-md border border-zinc-300 bg-white pl-10 pr-4 py-3 text-right text-lg font-bold tabular-nums text-zinc-900 outline-none transition-all focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 shadow-sm"
                    type="text"
                    placeholder={new Intl.NumberFormat("id-ID").format(total)}
                    value={amountPaid ? new Intl.NumberFormat("id-ID").format(amountPaid) : ""}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "");
                      setAmountPaid(val);
                    }}
                    autoFocus
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-4 gap-2">
                {quickAmounts.map((q) => (
                  <button
                    key={q.label}
                    onClick={() => { triggerHaptic("light"); setAmountPaid(q.val); }}
                    className="rounded-md border border-zinc-200 bg-white py-2 text-[11px] font-bold text-zinc-600 shadow-sm hover:border-zinc-900 hover:bg-zinc-900 hover:text-white transition-all focus:outline-none focus:ring-1 focus:ring-zinc-900"
                  >
                    {q.label}
                  </button>
                ))}
              </div>

              {parsed > 0 && (
                <div className={`mt-4 flex items-center justify-between rounded-md border px-4 py-3 transition-colors ${change < 0 ? "border-rose-200 bg-rose-50" : "border-zinc-200 bg-zinc-50 animate-fade-in"}`}>
                  <span className={`text-[13px] font-bold ${change < 0 ? "text-rose-600" : "text-zinc-600"}`}>
                    {change < 0 ? "Uang Kurang" : "Kembalian"}
                  </span>
                  <span className={`text-lg font-bold tabular-nums ${change < 0 ? "text-rose-600" : "text-zinc-900"}`}>
                    {fmtRp(Math.abs(change))}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="p-6 pt-0">
          <button
            onClick={handleConfirm}
            disabled={submitting || !isValid}
            className="w-full rounded-md bg-zinc-900 py-3.5 text-[14px] font-bold text-white shadow-sm transition-all hover:bg-zinc-800 disabled:opacity-40 disabled:scale-100 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:ring-offset-2"
          >
            {submitting ? "Memproses..." : "Konfirmasi Pembayaran"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function ConfirmModal({ title, message, confirmLabel, confirmTone, onClose, onConfirm }) {
  const [loading, setLoading] = useState(false);
  const handleConfirm = async () => {
    setLoading(true);
    try { await onConfirm(); } finally { setLoading(false); }
  };
  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-zinc-950/60 backdrop-blur-sm p-4 animate-fade-in">
      <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-2xl animate-fade-in text-center border border-zinc-200">
        <h2 className="text-lg font-bold text-zinc-900 mb-2">{title}</h2>
        <p className="text-[13px] font-medium text-zinc-500 mb-6">{message}</p>
        <div className="flex gap-3">
          <button onClick={onClose} className="w-1/2 rounded-md border border-zinc-200 bg-white py-2.5 text-[13px] font-bold text-zinc-700 hover:bg-zinc-50 transition-colors focus:outline-none focus:ring-1 focus:ring-zinc-900 shadow-sm">
            Batal
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className={`w-1/2 rounded-md py-2.5 text-[13px] font-bold text-white shadow-sm transition-all active:scale-95 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              confirmTone === "rose"
                ? "bg-rose-600 hover:bg-rose-700 focus:ring-rose-600"
                : "bg-zinc-900 hover:bg-zinc-800 focus:ring-zinc-900"
            }`}
          >
            {loading ? "Memproses..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function fmtRp(n) {
  return new Intl.NumberFormat("id-ID", {
    style: "currency", currency: "IDR", maximumFractionDigits: 0,
  }).format(Number(n) || 0);
}

export default Kasir;